/**
 * Tiny `BroadcastChannel` wrappers: subscribe in a hook, post one-shot messages
 * from a callback.
 *
 * Used for cross-tab signalling — currently the upvote channel so reaction
 * state stays in sync across multiple PSI tabs. Both helpers no-op if
 * `BroadcastChannel` is undefined (e.g. older webview environments). The post
 * helper opens and closes the channel for each message, so callers never have
 * to manage lifecycle.
 */

import { useEffect } from 'react';

export const VOTE_CHANNEL = 'psi-vote';

/**
 * Subscribe to messages on a named BroadcastChannel. The callback is called
 * with the message data whenever a message is received. The channel is
 * automatically closed on unmount.
 */
export function useBroadcastSubscription<T = unknown>(channelName: string, onMessage: (data: T) => void) {
    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') return;
        const channel = new BroadcastChannel(channelName);
        channel.onmessage = (event: MessageEvent<T>) => {
            onMessage(event.data);
        };
        return () => channel.close();
    }, [channelName, onMessage]);
}

/**
 * Post a message to a named BroadcastChannel. Opens the channel, sends the
 * message, and closes it immediately.
 */
export function broadcastMessage<T = unknown>(channelName: string, data: T) {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(channelName);
    channel.postMessage(data);
    channel.close();
}
